import { ref, reactive } from 'vue';
import { defineStore } from 'pinia';
import { IBalance, IToolUserSettings } from '@/types';
import { Currency, Platform } from '@/constants';
import { getPlatform } from '@/helpers/getPlatform';
import { currencySymbolDictionary } from '@/stores/simpleDictionary/currencySymbol';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale/en-US';
import { pt } from 'date-fns/locale/pt';
import { useWebSocketStore } from '@/stores/webSocket';

export const useUserStore = defineStore(
    'user',
    () => {
        const login = ref<string | null>(null);
        const type = ref<number | null>(null);
        const currency = reactive({
            id: Currency.Euro,
            symbol: currencySymbolDictionary.nameById(Currency.Euro),
        });
        const timeZone = ref<string>('Europe/London');
        const lang = ref<string | null>(null);
        const ownerId = ref<number | null>(null);
        const locale = ref<string | null>(null);

        const token = ref<string | null>(null);
        const balance = reactive<IBalance>({
            credit: 0,
            balance: 0,
            exposure: 0,
            credit_limit: 0,
            bet_balance: 0,
            deposit: 0,
        });

        let settings = reactive<IToolUserSettings>({
            is_play_sound_on_bet: 1,
            is_show_popup_on_bet: 1,
            is_show_credit: 1,
            is_show_balance: 1,
            is_show_exposure: 1,
            bet_order_dball_timeout_default: null, //kick off
            bet_order_is_keep_order_popup_open_default: 1,
            bet_order_is_max_prices_default: 1,
            bet_order_is_request_bet_confirmation_default: 1,
            bet_order_is_request_bet_duplicate_confirmation_default: 1,
            bet_order_is_suggested_prices_default: 1,
            bet_order_is_top_ups_default: 1,
            bet_order_live_timeout_default: null, //5 sec
            bet_order_odds_default_type_id: 1,
            bet_order_site_ids_allowed: [],
            bet_order_stake_default: null,
            bet_order_suggested_prices_default: [],
            favorite_game_order_by_id: 1, //asc
        });

        function setBalance(newBalance: Partial<IBalance>) {
            Object.assign(balance, newBalance);
        }

        function updateUserCredit(newBalance: Partial<IBalance>) {
            const webSocketStore = useWebSocketStore();
            setBalance(newBalance);
            webSocketStore.sendObj({
                event: 'updateUserCredit',
                data: {
                    credit: newBalance.credit,
                },
            });
        }

        function isLoggedIn(): boolean {
            return !!token.value && !!login.value;
        }

        function getTimezoneOffset(): string {
            //"+02:00"
            return formatInTimeZone(new Date(), timeZone.value, 'XXX');
        }

        const dateFnsLocale = computed(() => {
            //todo check it, lang does not work
            return lang.value === 'pt' ? pt : enUS;
        });

        return {
            login,
            type,
            currency,
            timeZone,
            lang,
            ownerId,
            locale,
            token,
            balance,
            settings,
            setBalance,
            updateUserCredit,
            isLoggedIn,
            getTimezoneOffset,
            dateFnsLocale,
        };
    },
    {
        persist: getPlatform() === Platform.Tradeball || localStorage.getItem('restoreUser') !== null,
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
